import React, { useMemo, useState } from 'react';
import type {
  SocialMediaPostResponseSchema,
  CreateSocialMediaPostRequestSchema,
  UpdateSocialMediaPostRequestSchema,
} from '@/api/client';
import { VideoPlayerModal } from '@/feature/play-video/VideoPlayerModal';
import { useAdminContext } from '../contexts/AdminProvider';

interface Props {
  posts: SocialMediaPostResponseSchema[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isPostingNow: boolean;
  editingId: number | null;
  onEdit: (id: number | null) => void;
  onCreate: (body: CreateSocialMediaPostRequestSchema) => Promise<void>;
  onUpdate: (id: number, body: UpdateSocialMediaPostRequestSchema) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onPostNow: (id: number) => Promise<void>;
}

const PLATFORMS = ['facebook', 'instagram'] as const;

const DEFAULT_CAPTION = `No editing. No AI. No BS.
A new kind of map made by people on the ground.
Drop a pin and add photos, voice, or video.
Go live from your location.

#authentic #communitymap #socialnetwork
#digitalburnout #socialmediafatigue`;

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'published':
      return 'admin-badge-success';
    case 'publishing':
      return 'admin-badge-info';
    case 'pending':
      return 'admin-badge-neutral';
    case 'failed':
      return 'admin-badge-error';
    case 'cancelled':
      return 'admin-badge-warning';
    default:
      return 'admin-badge-neutral';
  }
}

function toInputDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromInputDateTime(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

// Browser's current timezone abbreviation (e.g. "EST", "EDT", "PST"). Used to
// label datetime inputs and list columns so admins see exactly which zone the
// times are rendered in — `<input type="datetime-local">` has no built-in
// zone indicator.
function getLocalTimezoneAbbr(): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date());
  return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
}

interface FormData {
  videoUrl: string;
  caption: string;
  platforms: string[];
  scheduledAt: string;
}

function SocialMediaPostForm({
  initial,
  isEdit,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  initial?: FormData;
  isEdit: boolean;
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [videoUrl, setVideoUrl] = useState(initial?.videoUrl ?? '');
  const [caption, setCaption] = useState(initial?.caption ?? DEFAULT_CAPTION);
  const [platforms, setPlatforms] = useState<string[]>(initial?.platforms ?? ['facebook', 'instagram']);
  const [scheduledAt, setScheduledAt] = useState(initial?.scheduledAt ?? '');
  const tzAbbr = useMemo(() => getLocalTimezoneAbbr(), []);

  const togglePlatform = (platform: string) => {
    setPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ videoUrl, caption, platforms, scheduledAt });
  };

  return (
    <form onSubmit={handleSubmit} className="admin-card">
      <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {!isEdit && (
          <div className="admin-form-group" style={{ margin: 0 }}>
            <label className="admin-form-label">Video URL</label>
            <input
              className="admin-form-input admin-mono"
              value={videoUrl}
              onChange={e => setVideoUrl(e.target.value)}
              placeholder="https://..."
              required
            />
          </div>
        )}
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Caption</label>
          <textarea
            className="admin-form-input"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            rows={10}
            required
            style={{ resize: 'vertical' }}
          />
        </div>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Platforms</label>
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            {PLATFORMS.map(platform => (
              <label key={platform} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={platforms.includes(platform)}
                  onChange={() => togglePlatform(platform)}
                />
                <span style={{ textTransform: 'capitalize' }}>{platform}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">
            Scheduled At{tzAbbr && ` (${tzAbbr})`}
            {!isEdit && <span style={{ color: 'var(--admin-muted)', fontWeight: 400 }}> (optional — auto-queues if empty)</span>}
          </label>
          <input
            type="datetime-local"
            className="admin-form-input"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', paddingTop: 'var(--space-2)' }}>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={isSubmitting || platforms.length === 0}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );
}

export function SocialMediaView({
  posts,
  isLoading,
  isCreating,
  isUpdating,
  isDeleting,
  isPostingNow,
  editingId,
  onEdit,
  onCreate,
  onUpdate,
  onDelete,
  onPostNow,
}: Props) {
  const [showCreate, setShowCreate] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { showConfirmation } = useAdminContext();
  const tzAbbr = useMemo(() => getLocalTimezoneAbbr(), []);

  if (isLoading) {
    return <div className="admin-loading">Loading scheduled posts...</div>;
  }

  if (showCreate) {
    return (
      <SocialMediaPostForm
        isEdit={false}
        onSubmit={async (data) => {
          await onCreate({
            video_url: data.videoUrl,
            caption: data.caption,
            platforms: data.platforms,
            scheduled_at: fromInputDateTime(data.scheduledAt),
          });
          setShowCreate(false);
        }}
        onCancel={() => setShowCreate(false)}
        isSubmitting={isCreating}
      />
    );
  }

  if (editingId !== null) {
    const post = posts.find(p => p.id === editingId);
    if (!post) return null;

    return (
      <SocialMediaPostForm
        isEdit
        initial={{
          videoUrl: post.video_url,
          caption: post.caption,
          platforms: post.platforms,
          scheduledAt: toInputDateTime(post.scheduled_at),
        }}
        onSubmit={(data) =>
          onUpdate(editingId, {
            caption: data.caption,
            platforms: data.platforms,
            scheduled_at: fromInputDateTime(data.scheduledAt),
          })
        }
        onCancel={() => onEdit(null)}
        isSubmitting={isUpdating}
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <button className="admin-btn admin-btn-primary" onClick={() => setShowCreate(true)}>
          New Scheduled Post
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="admin-empty">
          <div className="admin-empty-title">No scheduled posts yet</div>
          <div className="admin-empty-description">Queue your first post to get started.</div>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Video</th>
                <th>Caption</th>
                <th>Platforms</th>
                <th>Scheduled</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id}>
                  <td>
                    <span className="admin-td-label">Video</span>
                    <div
                      onClick={() => setPreviewUrl(post.video_url)}
                      style={{
                        width: '72px',
                        height: '128px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: 'var(--admin-muted-bg, #000)',
                        position: 'relative',
                      }}
                    >
                      <video
                        src={post.video_url}
                        preload="metadata"
                        muted
                        playsInline
                        onLoadedMetadata={(e) => {
                          e.currentTarget.currentTime = 7;
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          pointerEvents: 'none',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(0,0,0,0.25)',
                          color: 'white',
                          fontSize: '24px',
                        }}
                      >
                        ▶
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="admin-td-label">Caption</span>
                    <span
                      className="admin-td-value"
                      style={{
                        fontWeight: 500,
                        color: 'var(--admin-fg)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        maxWidth: '320px',
                      }}
                    >
                      {post.caption}
                    </span>
                  </td>
                  <td>
                    <span className="admin-td-label">Platforms</span>
                    <span className="admin-td-value" style={{ textTransform: 'capitalize' }}>
                      {post.platforms.join(', ')}
                    </span>
                  </td>
                  <td>
                    <span className="admin-td-label">Scheduled{tzAbbr && ` (${tzAbbr})`}</span>
                    <span className="admin-td-value admin-mono">
                      {new Date(post.scheduled_at).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span className="admin-td-label">Status</span>
                    <span className={`admin-badge ${statusBadgeClass(post.status)}`}>
                      {post.status}
                    </span>
                    {post.last_error && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--admin-error)',
                          marginTop: 'var(--space-1)',
                          maxWidth: '240px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={post.last_error}
                      >
                        {post.last_error}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="admin-td-label">Attempts</span>
                    <span className="admin-td-value admin-mono">{post.attempts}</span>
                  </td>
                  <td>
                    <span className="admin-td-label">Actions</span>
                    <div className="admin-actions" style={{ flexDirection: 'row' }}>
                      <button
                        className="admin-btn admin-btn-sm admin-btn-secondary"
                        onClick={() => onEdit(post.id)}
                        disabled={post.status === 'publishing' || post.status === 'published'}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn admin-btn-sm admin-btn-primary"
                        disabled={isPostingNow || post.status === 'publishing' || post.status === 'published'}
                        onClick={() =>
                          showConfirmation(
                            `Post now to ${post.platforms.join(' and ')}?`,
                            () => onPostNow(post.id)
                          )
                        }
                      >
                        Post Now
                      </button>
                      <button
                        className="admin-btn admin-btn-sm admin-btn-danger"
                        disabled={isDeleting}
                        onClick={() =>
                          showConfirmation(
                            `Delete this scheduled post?`,
                            () => onDelete(post.id)
                          )
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewUrl && (
        <VideoPlayerModal
          isOpen={true}
          videoUrl={previewUrl}
          onClose={() => setPreviewUrl(null)}
        />
      )}
    </div>
  );
}
